import React from "react";
import { MEDIA_PREVIEW_TYPE } from "../../commonComponents/MediaPreview";
import { DATE_TIME_FORMATS, formatDateTime } from "../../../utils/dateTimes";

export const MEDIA_TRACKING_STATUS = {
  SENT: "SENT",
  OPENED: "OPENED",
};

export const MEDIA_TRACKING_STATUS_COLOR = {
  [MEDIA_TRACKING_STATUS.SENT]: "blue",
  [MEDIA_TRACKING_STATUS.OPENED]: "green",
};

export const MEDIA_TYPE = {
  IMAGE: "IMAGE",
  VIDEO: "VIDEO",
  DOC: "DOC",
};

export const LINK_DETAIL_FIELDS = [
  { key: "createTs", label: "Sent at", isDate: true },
  { key: "openCount", label: "Link opened", format: "openedCount" },
  { key: "firstOpenedAt", label: "Link first opened at", isDate: true },
  {
    key: "lastOpenedAt",
    label: "Link last opened at",
    isDate: true,
    showWhen: tracking => Number(tracking?.openCount) > 1,
  },
];

export const MEDIA_TYPE_LABELS = {
  [MEDIA_TYPE.IMAGE]: { singular: "Image", plural: "Images" },
  [MEDIA_TYPE.VIDEO]: { singular: "Video", plural: "Videos" },
  [MEDIA_TYPE.DOC]: { singular: "Document", plural: "Documents" },
};

export const getMediaTypeLabel = (type, count) => {
  const labels = MEDIA_TYPE_LABELS[type];
  if (!labels) return "";
  return `${count} ${count === 1 ? labels.singular : labels.plural}`;
};

export const MEDIA_DETAIL_STATUS = {
  OPENED: "OPENED",
  SENT: "SENT",
  WATCHED: "WATCHED",
  NOT_WATCHED: "NOT_WATCHED",
  VIEWED: "VIEWED",
  NOT_VIEWED: "NOT_VIEWED",
};

export const MEDIA_DETAIL_STATUS_COLOR = {
  [MEDIA_DETAIL_STATUS.OPENED]: "green",
  [MEDIA_DETAIL_STATUS.SENT]: "blue",
  [MEDIA_DETAIL_STATUS.WATCHED]: "green",
  [MEDIA_DETAIL_STATUS.NOT_WATCHED]: "orange",
  [MEDIA_DETAIL_STATUS.VIEWED]: "green",
  [MEDIA_DETAIL_STATUS.NOT_VIEWED]: "orange",
};

export const MEDIA_DETAIL_STATUS_LABEL = {
  [MEDIA_DETAIL_STATUS.OPENED]: "Opened",
  [MEDIA_DETAIL_STATUS.SENT]: "Sent",
  [MEDIA_DETAIL_STATUS.WATCHED]: "Watched",
  [MEDIA_DETAIL_STATUS.NOT_WATCHED]: "Not watched",
  [MEDIA_DETAIL_STATUS.VIEWED]: "Viewed",
  [MEDIA_DETAIL_STATUS.NOT_VIEWED]: "Not Viewed",
};

export const MEDIA_TYPE_DETAIL_FIELDS = {
  [MEDIA_TYPE.IMAGE]: [
    { key: "is_opened", countKey: "open_count", label: "Image viewed", format: "yesnoWithCount" },
    {
      key: "is_downloaded",
      countKey: "download_count",
      label: "Image downloaded",
      format: "yesnoWithCount",
    },
  ],
  [MEDIA_TYPE.VIDEO]: [
    { key: "is_opened", countKey: "open_count", label: "Video watched", format: "yesnoWithCount" },
    { key: "total_play_duration", label: "Watched time", format: "seconds" },
    {
      key: "is_downloaded",
      countKey: "download_count",
      label: "Video downloaded",
      format: "yesnoWithCount",
    },
  ],
  [MEDIA_TYPE.DOC]: [
    { key: "is_opened", countKey: "open_count", label: "Doc viewed", format: "yesnoWithCount" },
    {
      key: "is_downloaded",
      countKey: "download_count",
      label: "Doc downloaded",
      format: "yesnoWithCount",
    },
  ],
};

// API-side media type values (lowercase strings sent to/from the backend)
export const API_MEDIA_TYPE = Object.freeze({
  IMAGE: "image",
  VIDEO: "video",
  DOCUMENT: "document",
});

// Maps API `media_type` strings to MEDIA_TYPE enum keys
export const API_MEDIA_TYPE_MAP = {
  [API_MEDIA_TYPE.IMAGE]: MEDIA_TYPE.IMAGE,
  [API_MEDIA_TYPE.VIDEO]: MEDIA_TYPE.VIDEO,
  [API_MEDIA_TYPE.DOCUMENT]: MEDIA_TYPE.DOC,
};

// Reverse: maps MEDIA_TYPE enum to API string
export const MEDIA_TYPE_TO_API = {
  [MEDIA_TYPE.IMAGE]: API_MEDIA_TYPE.IMAGE,
  [MEDIA_TYPE.VIDEO]: API_MEDIA_TYPE.VIDEO,
  [MEDIA_TYPE.DOC]: API_MEDIA_TYPE.DOCUMENT,
};

// MEDIA_TYPE enum → shared MediaPreview type. Lives here so every surface that
// opens the previewer (tracking card, media detail drawer) maps types the same
// way instead of keeping its own copy.
export const MEDIA_TYPE_TO_PREVIEW_TYPE = {
  [MEDIA_TYPE.IMAGE]: MEDIA_PREVIEW_TYPE.IMAGE,
  [MEDIA_TYPE.VIDEO]: MEDIA_PREVIEW_TYPE.VIDEO,
  [MEDIA_TYPE.DOC]: MEDIA_PREVIEW_TYPE.DOC,
};

// Preview type for a raw API `media_type` string, defaulting to image so an
// unknown type still renders something rather than an empty stage.
export const getPreviewTypeFromApi = apiMediaType =>
  MEDIA_TYPE_TO_PREVIEW_TYPE[API_MEDIA_TYPE_MAP[String(apiMediaType || "").toLowerCase()]] ||
  MEDIA_PREVIEW_TYPE.IMAGE;

// Link tracking became available from this date — change here and the notice below
// updates automatically.
export const LINK_TRACKING_START_DATE = "2026-05-10";
export const LINK_TRACKING_NOTICE = (
  <>
    Media engagement tracking became available starting{" "}
    <strong>{formatDateTime(LINK_TRACKING_START_DATE, DATE_TIME_FORMATS.MMM_DD_YYYY)}</strong>.
    Media messages sent before this date will still display the delivery method (Link or MMS), but
    engagement metrics such as views, downloads, and video watch time are not available.
    <ul className="list-disc pl-5 mt-2">
      <li>
        <strong>Link:</strong> Used when the media file exceeds Twilio&apos;s maximum supported MMS
        size. The media is converted into a shortened URL and delivered as a text message instead of
        MMS.
      </li>
      <li>
        <strong>MMS:</strong> Media is sent directly as a multimedia message (MMS).
      </li>
    </ul>
  </>
);
